<template>
  <div class="tab-panel">
    <div class="bill-item">
      <div class="form-grid">
        <div class="form-group">
          <label>Title:</label>
          {{bicamBill.title}}
        </div>
        <div class="form-group">
          <label>Short Title:</label>
          {{bicamBill.short_title}}
        </div>
      </div>
      <div class="form-grid">
        <div class="form-group">
          <label>Bill Nos.:</label>
          {{bicamBill.nos}}
        </div>
        <div class="form-group">
          <label>Authors:</label>
          {{formatArray(bicamBill.authors)}}
        </div>
      </div>
      <div class="form-grid">
        <div class="form-group">
          <label>Remarks:</label>
          {{bicamBill.remarks}}
        </div>
        <div class="form-group">
          <label>Source/Link:</label>
          <a v-if="bicamBill.source" :href="bicamBill.source" target="_blank" class="link">{{ bicamBill.source }}</a>
        </div>
      </div>
      <div class="form-group">
        <label>Salient Features:</label>
        {{bicamBill.salient_features}}
      </div>

      <!-- Legislative Status Section -->
      <div class="subsection">
        <div class="subsection-header">
          <h6>Legislative Status</h6>
        </div>
        <div v-if="bicamBill.legisstatus?.length" v-for="(status, sidx) in bicamBill.legisstatus" :key="sidx" class="status-item">
          <div class="status-header">
            <span>Status #{{ sidx + 1 }}</span>
          </div>
          <div class="form-grid">
            <div class="form-group">
              <label>Adopted Version:</label>
              {{status.adopted_version}}
            </div>
            <div class="form-group">
              <label>Legislative Status:</label>
              {{status.legislative_status}}
            </div>
            <div class="form-group">
              <label>Date:</label>
              {{status.date}}
            </div>
          </div>
        </div>
        <div v-else class="no-data">No legislative status available</div>

      </div>

      <!-- Position Papers Section -->
      <PositionPaper 
        :positionPapers="bicamPositionPapers"
        :isDisabled="false"
        :statusOptions="['Bicam Conference Report']"
        @addPositionPaper="() => $emit('addPositionPaper', 'bicam')"
        @deletePositionPaper="(index) => $emit('deletePositionPaper', 'bicam', null, index)"
      />

      <!-- Bicam Key Issues Section -->
      <KeyIssuesSection 
        :keyIssues="keyIssues"
        :activeTab="keyIssuesActive"
        :revenueStartYear="revenueStartYear"
        :revenueEndYear="revenueEndYear"
        :budgetaryStartYear="budgetaryStartYear"
        :budgetaryEndYear="budgetaryEndYear"
        @update:activeTab="keyIssuesActive = $event"
        @update:keyIssues="$emit('update:keyIssues', $event)"
        @addRevenueRow="$emit('addBicamRevenueRow')"
        @addBudgetaryRow="$emit('addBicamBudgetaryRow')"
        @addDofConcernsRow="$emit('addBicamDofConcernsRow')"
        @deleteRevenueRow="$emit('deleteBicamRevenueRow', $event)"
        @deleteBudgetaryRow="$emit('deleteBicamBudgetaryRow', $event)"
        @deleteDofConcernsRow="$emit('deleteBicamDofConcernsRow', $event)"
        @getYearTotal="$emit('getYearTotal', $event)"
        @autoResize="$emit('autoResize', $event)"
        @updateRevenueStartYear="$emit('updateRevenueStartYear', $event)"
        @updateRevenueEndYear="$emit('updateRevenueEndYear', $event)"
        @updateBudgetaryStartYear="$emit('updateBudgetaryStartYear', $event)"
        @updateBudgetaryEndYear="$emit('updateBudgetaryEndYear', $event)"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import KeyIssuesSection from './KeyIssuesSectionView.vue'
import PositionPaper from './PositionPaperView.vue'

const props = defineProps({
  bicamBill: {
    type: Object,
    required: true
  },
  keyIssues: {
    type: Object,
    required: true
  },
  authors: {
    type: Array,
    default: () => []
  },
  revenueStartYear: {
    type: Number,
    default: () => new Date().getFullYear()
  },
  revenueEndYear: {
    type: Number,
    default: () => new Date().getFullYear() + 5
  },
  budgetaryStartYear: {
    type: Number,
    default: () => new Date().getFullYear()
  },
  budgetaryEndYear: {
    type: Number,
    default: () => new Date().getFullYear() + 5
  },
  positionPapers: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits([
  'addBicamRevenueRow',
  'addBicamBudgetaryRow',
  'addBicamDofConcernsRow',
  'deleteBicamRevenueRow',
  'deleteBicamBudgetaryRow',
  'deleteBicamDofConcernsRow',
  'getYearTotal',
  'autoResize',
  'addPositionPaper',
  'deletePositionPaper',
  'update:keyIssues',
  'updateRevenueStartYear',
  'updateRevenueEndYear',
  'updateBudgetaryStartYear',
  'updateBudgetaryEndYear'
])

const formatArray = (value) => {
  if (Array.isArray(value)) {
    return value.join(', ')
  }
  return value || '-'
}

const keyIssuesActive = ref('revenue')

const bicamPositionPapers = computed(() => {
  return props.positionPapers.filter(p => p.type === 'bicam')
})
</script>

<style scoped>
.bill-item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #059669;
  font-size: 0.9rem;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.3s;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #059669;
  box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.1);
}

.subsection {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
}

.subsection-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.subsection-header h6 {
  color: #059669;
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0;
}

.status-item {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.status-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
}

.add-sub-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.add-sub-btn:hover {
  background: linear-gradient(135deg, #047857 0%, #10b981 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.delete-sub-btn {
  background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 0.8rem;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
}

.delete-sub-btn:hover {
  background: linear-gradient(135deg, #b91c1c 0%, #dc2626 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.4);
}

.link {
  word-wrap: break-word;
  word-break: break-all;
  overflow-wrap: break-word;
  hyphens: auto;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .subsection-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .status-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}
</style>